<script setup lang="ts">
import { ref } from 'vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import {
  useAccountDetails,
  useToastNotifications,
} from '@apparatix/composables';
import { AccountNotes } from '@apparatix/schemas/AccountNotes';
import { useAccountStore } from '@apparatix/stores/useAccountStore';
import AccountNoteEditorForm from '@apparatix/leads/AccountNoteEditorForm.vue';
import AccountNoteViewForm from '@apparatix/leads/AccountNoteViewForm.vue';
import DetailEditorControls from '@apparatix/library/DetailEditorControls.vue';

const store = useAccountStore();
const confirm = useConfirm();
const { showSuccess } = useToastNotifications();

const {
  accountNotesSchema,
} = AccountNotes();

const {
  defineField, meta, resetForm, errors: accountNoteEditorErrors, values: accountNoteEditorModel,
} = useForm({
  validationSchema: toTypedSchema(accountNotesSchema),
  initialValues: {
    id: store.accountDetails.id,
    accountNotes: store.accountDetails.accountNotes,
    arNotes: store.accountOverview.arNotes,
  },
});

const detail = useAccountDetails();

const [accountNotes] = defineField('accountNotes');
const [arNotes] = defineField('arNotes');

const isEditing = ref(false);

const onSave = async () => {
  // This is a bit dirty - we could set this value in the endpoint.
  // It's only updated here and reduces other necessary changes.
  // The value is not returned from the AccountUpdate endpoint.
  store.accountOverview.arNotes = accountNoteEditorModel.arNotes;
  await detail.sendAccountUpdate(accountNoteEditorModel);
  showSuccess('Account Notes Saved', 'Success');
  isEditing.value = false;
  resetForm({
    values: {
      accountNotes: store.accountDetails.accountNotes,
      arNotes: store.accountOverview.arNotes,
    },
  });
};

const onCancelEditing = () => {
  if (meta.value.dirty) {
    confirm.require({
      header: ' ',
      group: 'confirmNotesClose',
      message: 'You have unsaved changes. Do you want to continue?',
      accept: () => onConfirmCancel(),
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      rejectClass: 'p-button-secondary',
    });
    return;
  }
  onConfirmCancel();
};

const onConfirmCancel = () => {
  resetForm();
  isEditing.value = false;
};

</script>
<template>
  <DetailEditorControls
    header="Notes"
    :is-editing="isEditing"
    :disable-save="Boolean(Object.keys(accountNoteEditorErrors).length) || !meta.dirty"
    @on:edit-click="isEditing = true"
    @on:cancel-click="onCancelEditing"
    @on:save-click="onSave"
  >
    <AccountNoteViewForm
      v-if="!isEditing"
      :errors="accountNoteEditorErrors"
    />
    <AccountNoteEditorForm
      v-if="isEditing"
      v-model="accountNoteEditorModel"
      v-model:account-notes="accountNotes"
      v-model:ar-notes="arNotes"
      :errors="accountNoteEditorErrors"
    />
  </DetailEditorControls>
  <ConfirmDialog group="confirmNotesClose" />
</template>
